import Swal from 'sweetalert2';
import axios from "axios";
// 2026-06-25: Phase 2 — WO Workbench (supervisor view).
// Left = WO list with filters; Right = selected WO detail with operations + release/seed actions.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listWorkOrders, getWorkOrderDetail, releaseWorkOrder,
  createWorkOrder, updateWorkOrder, deleteWorkOrder, seedDemo, listWorkCenters,
} from '../../Services/app/workorderservice';
import { customerbasedshift, cleanCustomerId } from '../../Services/app/operatorservice';
import { shiftadd } from '../../Services/app/masterservice';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', red: '#dc2626',
  amber: '#f59e0b', dark: '#111827', muted: '#6b7280', border: '#e5e7eb',
  bg: '#f9fafb',
};

const STATUS_COLOR = {
  DRAFT: { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' },
  RELEASED: { bg: '#dbeafe', text: '#1e3a8a', border: '#3b82f6' },
  IN_PROGRESS: { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
  ON_HOLD: { bg: '#fed7aa', text: '#7c2d12', border: '#ea580c' },
  COMPLETE: { bg: '#dcfce7', text: '#14532d', border: '#16a34a' },
  CANCELLED: { bg: '#fee2e2', text: '#7a0000', border: '#dc2626' },
  PENDING: { bg: '#f3f4f6', text: '#6b7280', border: '#9ca3af' },
  READY: { bg: '#dbeafe', text: '#1e3a8a', border: '#3b82f6' },
  CLAIMED: { bg: '#dcfce7', text: '#14532d', border: '#16a34a' },
  DONE: { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
  SKIPPED: { bg: '#f3f4f6', text: '#6b7280', border: '#9ca3af' },
};

const PRIO_COLOR = {
  URGENT: '#dc2626', HIGH: '#f97316', NORMAL: '#2563eb', LOW: '#6b7280',
};

// Phase 13 2026-06-26: rename CLAIMED/DONE to COMPLETED in the UI
const STATUS_LABEL = {
  CLAIMED:  'COMPLETED',
  DONE:     'COMPLETED',
  COMPLETE: 'COMPLETED',
  IN_PROGRESS: 'RUNNING',
};

function ProcChart({ pts, keyName, color, label, unit, mn, mx, startMs }) {
  const [hi, setHi] = useState(null);
  const W = 250, H = 132, L = 34, R = 10, T = 10, B = 36;
  const vals = pts.map(p => p[keyName]);
  const mmin = Math.min(...vals), mmax = Math.max(...vals), avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  const xs = (i) => L + (i / (pts.length - 1)) * (W - L - R);
  const ys = (v) => T + (1 - ((v - mn) / (mx - mn))) * (H - T - B);
  const clk = (i) => { const d = new Date(startMs + pts[i].t * 60000); return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0'); };
  const d = pts.map((p, i) => (i ? 'L' : 'M') + xs(i).toFixed(1) + ' ' + ys(p[keyName]).toFixed(1)).join(' ');
  const onMove = (e) => { const r = e.currentTarget.getBoundingClientRect(); const x = (e.clientX - r.left) * (W / r.width); let idx = Math.round((x - L) / ((W - L - R) / (pts.length - 1))); idx = Math.max(0, Math.min(pts.length - 1, idx)); setHi(idx); };
  return (<div style={{ flex: '1 1 230px', maxWidth: 320 }}>
    <div style={{ fontSize: 11, fontWeight: 800, color: '#374151' }}>{label}</div>
    <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 2 }}>min {mmin}{unit} · max {mmax}{unit} · avg {avg.toFixed(1)}{unit}</div>
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} onMouseMove={onMove} onMouseLeave={() => setHi(null)} style={{ display: 'block', border: '1px solid #eef2f7', borderRadius: 6, background: '#fff', cursor: 'crosshair' }}>
      <line x1={L} y1={T} x2={L} y2={H - B} stroke="#e5e7eb" />
      <line x1={L} y1={H - B} x2={W - R} y2={H - B} stroke="#e5e7eb" />
      <text x={L - 4} y={T + 4} textAnchor="end" fontSize="8" fill="#9ca3af">{mx}</text>
      <text x={L - 4} y={H - B} textAnchor="end" fontSize="8" fill="#9ca3af">{mn}</text>
      <text x={L} y={H - B + 12} fontSize="8" fill="#9ca3af">{clk(0)}</text>
      <text x={W - R} y={H - B + 12} textAnchor="end" fontSize="8" fill="#9ca3af">{clk(pts.length - 1)}</text>
      <text x={(L + W - R) / 2} y={H - B + 24} textAnchor="middle" fontSize="8" fill="#9ca3af">Time (from → to)</text>
      <path d={d} fill="none" stroke={color} strokeWidth="2" />
      {pts.map((p, i) => (<circle key={i} cx={xs(i)} cy={ys(p[keyName])} r="2.5" fill={color} />))}
      {hi != null && (<g>
        <line x1={xs(hi)} y1={T} x2={xs(hi)} y2={H - B} stroke={color} strokeDasharray="3 3" opacity="0.5" />
        <circle cx={xs(hi)} cy={ys(pts[hi][keyName])} r="4" fill={color} stroke="#fff" strokeWidth="1.5" />
        <rect x={Math.min(Math.max(xs(hi) - 33, 0), W - 68)} y={4} width="66" height="20" rx="3" fill="#111827" />
        <text x={Math.min(Math.max(xs(hi) - 30, 3), W - 65)} y={13} fontSize="8" fill="#cbd5e1">{clk(hi)}</text>
        <text x={Math.min(Math.max(xs(hi) - 30, 3), W - 65)} y={21} fontSize="9" fontWeight="700" fill="#fff">{pts[hi][keyName]}{unit}</text>
      </g>)}
    </svg>
  </div>);
}

function procParams(op) {
  const n = (op.op_name || '').toLowerCase();
  if (n.includes('anodiz') || n.includes('bsa') || n.includes('chromic') || n.includes('sulphuric')) return [
    { k: 'voltage', label: 'Voltage (V)', unit: ' V', color: '#2563eb', mn: 0, mx: 16, gen: (b, i) => Math.min(15, +(i * 2.5 + b * 0.2).toFixed(1)) },
    { k: 'current', label: 'Current (A)', unit: ' A', color: '#f97316', mn: 0, mx: 14, gen: (b, i) => +(12 - i * 0.35 + Math.sin(i + b) * 0.4).toFixed(1) },
    { k: 'ph', label: 'Bath pH', unit: '', color: '#7c3aed', mn: 0, mx: 3, gen: (b, i) => +(0.9 + Math.sin(i * 0.6 + b) * 0.15).toFixed(2) },
    { k: 'temp', label: 'Bath Temperature (°C)', unit: ' °C', color: '#dc2626', mn: 15, mx: 25, gen: (b, i) => +(20 + Math.sin(i * 0.7 + b) * 0.9).toFixed(1) },
  ];
  if (n.includes('fpi') || n.includes('penetrant') || (n.includes('ndt') && !n.includes('clean'))) return [
    { k: 'dwell', label: 'Penetrant Dwell (min)', unit: ' min', color: '#7c3aed', mn: 0, mx: 20, gen: (b, i) => Math.min(15, +(i * 2).toFixed(1)) },
    { k: 'dev', label: 'Developer Time (min)', unit: ' min', color: '#0891b2', mn: 0, mx: 12, gen: (b, i) => Math.min(10, +(i * 1.3).toFixed(1)) },
    { k: 'uv', label: 'UV Intensity (µW/cm²)', unit: '', color: '#16a34a', mn: 900, mx: 1300, gen: (b, i) => Math.round(1000 + Math.sin(i + b) * 60 + i * 8) },
    { k: 'temp', label: 'Temperature (°C)', unit: ' °C', color: '#dc2626', mn: 15, mx: 32, gen: (b, i) => +(24 + Math.sin(i * 0.5 + b) * 1.2).toFixed(1) },
  ];
  if (n.includes('mpi') || n.includes('magnetic')) return [
    { k: 'magI', label: 'Magnetizing Current (A)', unit: ' A', color: '#f97316', mn: 0, mx: 3500, gen: (b, i) => Math.round(2800 + Math.sin(i + b) * 180) },
    { k: 'field', label: 'Field Strength (Gauss)', unit: ' G', color: '#2563eb', mn: 0, mx: 60, gen: (b, i) => Math.round(40 + Math.sin(i * 0.6 + b) * 6) },
    { k: 'uv', label: 'UV Intensity (µW/cm²)', unit: '', color: '#16a34a', mn: 900, mx: 1300, gen: (b, i) => Math.round(1000 + i * 10) },
  ];
  if (n.includes('clean')) return [
    { k: 'ust', label: 'Ultrasonic Time (min)', unit: ' min', color: '#0891b2', mn: 0, mx: 12, gen: (b, i) => Math.min(10, +(i * 1.3).toFixed(1)) },
    { k: 'temp', label: 'Bath Temperature (°C)', unit: ' °C', color: '#dc2626', mn: 40, mx: 70, gen: (b, i) => +(55 + Math.sin(i * 0.5 + b) * 3).toFixed(1) },
    { k: 'conc', label: 'Concentration (%)', unit: ' %', color: '#7c3aed', mn: 0, mx: 30, gen: (b, i) => +(23 + Math.sin(i + b) * 1.2).toFixed(1) },
  ];
  if (n.includes('passiv') || n.includes('conversion') || n.includes('alodine')) return [
    { k: 'temp', label: 'Temperature (°C)', unit: ' °C', color: '#dc2626', mn: 15, mx: 40, gen: (b, i) => +(30 + Math.sin(i * 0.5 + b) * 2).toFixed(1) },
    { k: 'ph', label: 'pH', unit: '', color: '#7c3aed', mn: 0, mx: 4, gen: (b, i) => +(1.5 + Math.sin(i + b) * 0.2).toFixed(2) },
    { k: 'time', label: 'Immersion Time (min)', unit: ' min', color: '#0891b2', mn: 0, mx: 5, gen: (b, i) => Math.min(4, +(i * 0.5).toFixed(1)) },
  ];
  if (n.includes('jig')) return [];
  return [
    { k: 'temp', label: 'Temperature (°C)', unit: ' °C', color: '#dc2626', mn: 15, mx: 30, gen: (b, i) => +(22 + Math.sin(i + b)).toFixed(1) },
    { k: 'time', label: 'Process Time (min)', unit: ' min', color: '#0891b2', mn: 0, mx: 35, gen: (b, i) => i * 4 },
  ];
}

function Pill({ children, color }) {
  const c = STATUS_COLOR[children] || STATUS_COLOR.DRAFT;
  const label = STATUS_LABEL[children] || children;
  return (
    <span style={{
      padding: '3px 10px', fontSize: 12, fontWeight: 800, letterSpacing: 0.5,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 4,
    }}>{label}</span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); }
  catch { return '—'; }
}



// Phase 12 v12: suggest machines whose capabilities cover ALL of this row's op_types.
function SuggestedMachines({ op, machineCaps }) {
  if (!op?.op_type) return null;
  const codes = op.op_type.split(',').filter(Boolean);
  if (codes.length === 0) return null;
  const matches = Object.entries(machineCaps || {})
    .filter(([, caps]) => Array.isArray(caps) && codes.every(c => caps.includes(c)))
    .map(([name]) => name);
  return (
    <div style={{ fontSize: 11, color: matches.length ? '#16a34a' : '#dc2626',
                  marginBottom: 8, paddingLeft: 76, fontWeight: 600 }}>
      {matches.length === 0
        ? `⚠ No machine in this shop can do all of: ${codes.join(' + ')}`
        : `✓ Suggested: ${matches.slice(0, 3).join(', ')}${matches.length > 3 ? ` +${matches.length - 3} more` : ''}`
      }
    </div>
  );
}

// Phase 12 v11: multi-select op picker — checkboxes in a floating panel.
function ComponentCombo({ value, components, onPick, placeholder, small }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [rect, setRect] = React.useState(null);
  const ref = React.useRef(null);
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const openNow = () => { if (inputRef.current) setRect(inputRef.current.getBoundingClientRect()); setQuery(''); setOpen(true); };
  const sel = (components || []).find(c => String(c.component_number || '') === String(value || ''));
  const display = value ? (sel ? `${value} · ${sel.component_name || ''}` : String(value)) : '';
  const q = query.toLowerCase().trim();
  const list = (components || []).filter(c => {
    if (!q) return true;
    return String(c.component_number || '').toLowerCase().includes(q) || String(c.component_name || '').toLowerCase().includes(q);
  }).slice(0, 80);
  return (
    <div ref={ref} style={{ position: 'relative', width: '100%', minWidth: 0 }}>
      <input ref={inputRef} value={open ? query : display}
             onChange={e => { setQuery(e.target.value); setOpen(true); }}
             onFocus={openNow}
             placeholder={placeholder}
             style={{ width: '100%', boxSizing: 'border-box', height: 32, padding: '6px 9px', border: `1px solid ${BR.border}`, borderRadius: 4, fontSize: small ? 12 : 13 }} />
      {open && rect && (
        <div style={{ position: 'fixed', top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 240), zIndex: 4000, maxHeight: 240, overflowY: 'auto', background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 6, boxShadow: '0 10px 28px rgba(0,0,0,0.20)' }}>
          {list.length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: BR.muted, fontWeight: 600 }}>No match</div>
          ) : list.map(c => (
            <div key={c.component_number}
                 onMouseDown={(ev) => { ev.preventDefault(); onPick(String(c.component_number)); setQuery(''); setOpen(false); }}
                 onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                 onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                 style={{ padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
              <span style={{ fontSize: 12, fontWeight: 800, fontFamily: 'monospace', color: BR.dark }}>{c.component_number}</span>
              <span style={{ fontSize: 11, color: BR.muted }}> {c.component_name || ''}</span>
              {c.cycle_time ? <span style={{ fontSize: 11, color: BR.blue, fontWeight: 700 }}> · {c.cycle_time}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SpecialProcessPicker({ op, spRecipes, value, onChange }) {
  if (!spRecipes || spRecipes.length === 0) return null;
  return (
    <div style={{ margin: '0 0 8px 52px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: BR.muted }}>Special Process:</span>
      <select value={value || ''} onChange={e => onChange(e.target.value)}
              style={{ padding: '3px 6px', border: `1px solid ${BR.border}`, borderRadius: 4, fontSize: 11 }}>
        <option value="">— none —</option>
        {spRecipes.map(sp => { const c = sp.code || sp.process; return <option key={c} value={c}>{c}{sp.name ? ` · ${sp.name}` : ''}</option>; })}
      </select>
      {value && <span style={{ fontSize: 10, color: BR.orange, fontWeight: 700 }}>→ Follower will be generated</span>}
    </div>
  );
}

function RecipePicker({ op, recipeMaster, value, onChange }) {
  const codes = (op?.op_type || '').split(',').map(s => s.trim()).filter(Boolean);
  if (!codes.length) return null;
  const matches = (recipeMaster || []).filter(r =>
    r && String(r.status || '').toUpperCase() === 'ACTIVE' && codes.includes(r.op_type));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '2px 0 6px 76px' }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Recipe</span>
      {matches.length === 0 ? (
        <span style={{ fontSize: 11, color: '#9ca3af' }}>no active recipe for {codes.join('/')}</span>
      ) : (
        <select value={value || ''} onChange={e => onChange(e.target.value)}
                style={{ padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 11, minWidth: 230 }}>
          <option value="">— none (optional) —</option>
          {matches.map(r => (
            <option key={r.id || r.recipe_code} value={r.recipe_code}>
              {r.recipe_code}{r.revision ? ` rev ${r.revision}` : ''}{r.recipe_name ? ` · ${r.recipe_name}` : ''} ({(r.parameters || []).filter(pp => pp.name).length}p)
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function MultiOpPicker({ shop, value, operationMaster, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef();

  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const selectedCodes = (value || '').split(',').filter(Boolean);
  const ops = (operationMaster || [])
    .filter(o => o.shop_type === shop && o.active !== false)
    .sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));

  const toggle = (code) => {
    const next = selectedCodes.includes(code)
      ? selectedCodes.filter(c => c !== code)
      : [...selectedCodes, code];
    // Stable order: respect operationMaster sort_order
    const orderedCodes = ops.filter(o => next.includes(o.code)).map(o => o.code);
    const orderedNames = ops.filter(o => next.includes(o.code)).map(o => o.name);
    onChange(orderedCodes, orderedNames);
  };

  const displayLabel = (() => {
    if (!shop) return '— Pick shop first —';
    if (selectedCodes.length === 0) return '— Pick operation(s) —';
    const names = ops.filter(o => selectedCodes.includes(o.code)).map(o => o.name);
    if (names.length === 1) return names[0];
    return names.join(' + ');
  })();

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button"
              onClick={() => shop && setOpen(o => !o)}
              disabled={!shop}
              style={{ width: '100%', padding: 6, border: '1px solid #e5e7eb',
                       borderRadius: 4, fontSize: 12, background: shop ? '#fff' : '#f3f4f6',
                       textAlign: 'left', cursor: shop ? 'pointer' : 'not-allowed',
                       color: selectedCodes.length === 0 ? '#9ca3af' : '#111827',
                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {displayLabel} {shop && <span style={{ float: 'right', fontSize: 10, color: '#9ca3af' }}>▾</span>}
      </button>
      {open && (
        <div style={{ position: 'absolute', bottom: '100%', right: 0, marginBottom: 4,
                      background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                      boxShadow: '0 -4px 12px rgba(0,0,0,0.15)', zIndex: 1000,
                      width: 220, maxHeight: 220, overflowY: 'auto', padding: 4 }}>
          <div style={{ padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#6b7280',
                        textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #f3f4f6' }}>
            Select one or more ({selectedCodes.length} chosen)
          </div>
          {ops.length === 0 ? (
            <div style={{ padding: 12, color: '#9ca3af', fontSize: 12 }}>No operations for this shop.</div>
          ) : ops.map(op => (
            <label key={op.code} style={{ display: 'flex', alignItems: 'center', padding: '6px 10px',
                                          fontSize: 12, cursor: 'pointer', borderRadius: 4 }}
                   onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                   onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <input type="checkbox"
                     checked={selectedCodes.includes(op.code)}
                     onChange={() => toggle(op.code)}
                     style={{ marginRight: 8 }} />
              <span style={{ width: 8, height: 8, borderRadius: '50%',
                             background: op.color || '#9ca3af', marginRight: 6, display: 'inline-block' }} />
              {op.name}
            </label>
          ))}
          <button onClick={() => setOpen(false)}
                  style={{ width: '100%', marginTop: 6, padding: '6px', background: '#f97316',
                           color: '#fff', border: 'none', borderRadius: 4, fontSize: 11,
                           fontWeight: 700, cursor: 'pointer' }}>Done</button>
        </div>
      )}
    </div>
  );
}

function CreateModal({ open, onClose, onCreated, customerId, workCenters, operationMaster, machineCaps, recipeMaster, components, spRecipes, opSetupMaster }) {
  // Phase 12 v10: group WCs by shop with ops preview ("Machine Shop · Milling, Turning, Drilling…")
  const shopGroups = React.useMemo(() => {
    const seenShops = new Set();
    const out = [];
    workCenters.forEach(w => seenShops.add(w.shop_type));
    seenShops.forEach(shop => {
      const wcsHere = workCenters.filter(w => w.shop_type === shop);
      const labelBase = wcsHere[0]?.name?.split('—')[0]?.trim() || shop;
      // Operations for this shop, name list
      const opNames = operationMaster
        .filter(op => op.shop_type === shop && op.active !== false)
        .sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99))
        .map(op => op.name);
      const opsPreview = opNames.length === 0 ? '' :
        opNames.length <= 3 ? opNames.join(', ') :
        opNames.slice(0, 3).join(', ') + ` +${opNames.length - 3} more`;
      out.push({ shop_type: shop, label: labelBase, opsPreview, wcCount: wcsHere.length });
    });
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [workCenters, operationMaster]);

  const [form, setForm] = useState({
    wo_number: '', part_number: '', part_name: '', qty_ordered: 100, due_date: '',
    priority: 'NORMAL', customer_po: '', end_customer: '',
  });
  const [ops, setOps] = useState([{ op_seq: 'OP10', op_name: '', op_type: '', work_center_code: '', work_center_shop_type: '', recipe_code: '', component_number: '', planned_time_min: 10 }]);
  const parseCycle = (ct) => { if (!ct) return 0; const [h = 0, m = 0, s = 0] = String(ct).trim().split(':').map(Number); return (h * 3600) + (m * 60) + (s || 0); };
  const compOf = (code) => (components || []).find(x => String(x.component_number || '').trim() === String(code || '').trim());
  const perPartSec = (code) => { const c = compOf(code); return c ? { mc: parseCycle(c.cycle_time), hd: parseCycle(c.handling_time) } : { mc: 0, hd: 0 }; };
  const minsFor = (code, qty) => { const { mc, hd } = perPartSec(code); const per = mc + hd; return (per > 0 && qty > 0) ? Math.max(1, Math.round((per * qty) / 60)) : null; };
  const fmtSec = (s) => { s = Math.round(s || 0); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60; return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(x).padStart(2, '0')}` : `${m}:${String(x).padStart(2, '0')}`; };
  const pickOpComponent = (i, code) => {
    updateOp(i, 'component_number', code);
    const mins = minsFor(code, parseInt(form.qty_ordered, 10) || 0);
    if (mins != null) updateOp(i, 'planned_time_min', mins);
    if (i === 0) { const c = compOf(code); setForm(f => ({ ...f, part_number: code, part_name: c ? (c.component_name || f.part_name) : f.part_name })); }
  };
  const recomputeAllOpTimes = (qty) => {
    setOps(prev => prev.map(o => { const mins = minsFor(o.component_number, qty); return mins != null ? { ...o, planned_time_min: mins } : o; }));
  };
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [routeComp, setRouteComp] = useState('');

  if (!open) return null;

  const addOp = () => {
    const nextSeq = `OP${(ops.length + 1) * 10}`;
    setOps(prev => [...prev, { op_seq: nextSeq, op_name: '', op_type: '', work_center_code: '', work_center_shop_type: '', recipe_code: '', component_number: '', planned_time_min: 10 }]);
  };
  const updateOp = (i, k, v) => setOps(prev => prev.map((o, idx) => idx === i ? { ...o, [k]: v } : o));
  const removeOp = (i) => setOps(prev => prev.filter((_, idx) => idx !== i));

  const loadRouteFromComponent = (code) => {
    const c = compOf(code);
    if (!c) return;
    setForm(f => ({ ...f, part_number: c.component_number || code, part_name: c.component_name || f.part_name }));
    const compOps = Array.isArray(c.operations) ? c.operations : [];
    if (compOps.length === 0) { setErr('This component has no operations yet. Add operations to it in the Component library first.'); return; }
    setErr(null);
    const qty = parseInt(form.qty_ordered, 10) || 0;
    const hhmmssToSec = (t) => { if (!t) return 0; const a = String(t).split(':').map(Number); return (a[0] || 0) * 3600 + (a[1] || 0) * 60 + (a[2] || 0); };
    const mapped = compOps.map((op, idx) => {
      const perSec = hhmmssToSec(op.cycle_time) + hhmmssToSec(op.handling_time) + hhmmssToSec(op.machine_time);
      const planned = (perSec > 0 && qty > 0) ? Math.max(1, Math.round((perSec * qty) / 60)) : 0;
      return {
        op_seq: `OP${(idx + 1) * 10}`,
        op_name: op.operation_name || op.name || `Op ${(idx + 1) * 10}`,
        op_type: '',
        work_center_shop_type: '',
        work_center_code: '',
        recipe_code: '',
        component_number: code,
        planned_time_min: planned,
        op_code: op.operation_number || '',
        ref_docs: [],
        instruction: '',
        sampling_qty: '',
        wi_report_no: '',
        remarks: '',
        special_process: '',
        internal_spec: '',
        spec_rev: '',
      };
    });
    setOps(mapped);
  };

  async function generateRouteArtifacts(woResult) {
    try {
      const [rcRes, frRes, spRes] = await Promise.all([
        customerbasedshift(customerId, 'route_cards').catch(() => []),
        customerbasedshift(customerId, 'follower_records').catch(() => []),
        customerbasedshift(customerId, 'sp_recipes').catch(() => []),
      ]);
      const cards = Array.isArray(rcRes && rcRes[0] && rcRes[0].value) ? rcRes[0].value : [];
      const followers = Array.isArray(frRes && frRes[0] && frRes[0].value) ? frRes[0].value : [];
      const spRecipes = Array.isArray(spRes && spRes[0] && spRes[0].value) ? spRes[0].value : [];
      const recipeByCode = {}; spRecipes.forEach(r => { recipeByCode[r.code || r.process] = r; });
      const woId = (woResult && woResult.id) || '';
      const woNumber = (woResult && woResult.wo_number) || form.wo_number;
      const rcId = `rc-wo-${woId || woNumber}`;
      const routeOps = ops.map((o, idx) => ({
        op_no: (o.op_seq || '').replace(/^OP/, '') || String((idx + 1) * 10),
        code: o.op_code || o.op_type || '',
        name: o.op_name || '',
        special_process: o.special_process || '',
        internal_spec: o.internal_spec || '',
        spec_rev: o.spec_rev || '',
        ref_docs: Array.isArray(o.ref_docs) ? o.ref_docs : [],
        instruction: o.instruction || '',
        sampling_qty: o.sampling_qty || '',
        wi_report_no: o.wi_report_no || '',
        remarks: o.remarks || '',
      }));
      const spList = routeOps.filter(op => op.special_process);
      const existingIds = new Set(followers.map(f => f.id));
      const followerIds = [];
      const toAdd = [];
      spList.forEach(op => {
        const fid = `flw-${rcId}-${op.op_no}`;
        followerIds.push(fid);
        if (existingIds.has(fid)) return;
        const recipe = recipeByCode[op.special_process];
        toAdd.push({
          id: fid, route_card_no: `RC-${woNumber}`, wo_id: woId, wo_number: woNumber,
          part_no: form.part_number || '', part_rev: '', qty: parseInt(form.qty_ordered, 10) || '',
          recipe_code: op.special_process, recipe_name: (recipe && recipe.name) || op.special_process, commodity: (recipe && recipe.commodity) || '',
          op_no: op.op_no, op_code: op.code, op_name: op.name,
          operator_name: '', operator_stamp: '', operator_level: '', rpg_no: '', batch_no: '', batch_qty: '', timer_id: '',
          status: 'OPEN', started_ts: '', completed_ts: '', readings: [], out_of_limit_count: 0, gate_fail_count: 0,
          qa_signoff_by: '', qa_signoff_ts: '', qa_disposition: '', qa_note: '',
          source_route_card: rcId, created_ts: new Date().toISOString(), updated_ts: '',
        });
      });
      const rc = {
        id: rcId, route_card_no: `RC-${woNumber}`, route_no: '', route_name: form.part_name || '',
        wo_id: woId, wo_number: woNumber, part_no: form.part_number || '', part_rev: '', qty: parseInt(form.qty_ordered, 10) || '',
        issued_by: 'WO create', issued_ts: new Date().toISOString(),
        operations: routeOps, follower_ids: followerIds, auto_generated: true,
        created_ts: new Date().toISOString(), updated_ts: '',
      };
      const nextCards = [...cards.filter(c => c.wo_id !== woId && c.id !== rcId), rc];
      await shiftadd({ route_cards: nextCards }, customerId, 'SERVER_SCOPE');
      if (toAdd.length) await shiftadd({ follower_records: [...followers, ...toAdd] }, customerId, 'SERVER_SCOPE');
    } catch (e) {
      console.error('WO route/follower auto-generate failed', e);
    }
  }

  // "OP10 means all OP10" — new operations pick up whatever the Operation Setup screen has
  // configured for their position; positions not in that list default to setup-enabled.
  const setupEnabledForSeq = (seq) => {
    const m = (opSetupMaster || []).find(s => s.op_seq === seq);
    return m ? (m.setup_enabled !== false) : true;
  };

  async function save() {
    if (!form.wo_number) { setErr('WO Number required'); return; }
    if (ops.some(o => !o.op_name || !o.work_center_code)) { setErr('Every operation needs name + work center'); return; }
    setSaving(true); setErr(null);
    try {
      const r = await createWorkOrder({
        customer_id: customerId, ...form,
        qty_ordered: parseInt(form.qty_ordered, 10) || 0,
        operations: ops.map(o => ({ ...o, planned_time_min: parseFloat(o.planned_time_min) || 0, setup_enabled: setupEnabledForSeq(o.op_seq) })),
      });
      await generateRouteArtifacts(r);
      onCreated && onCreated(r);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.message || e.message);
    } finally { setSaving(false); }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999,
                                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 720, maxHeight: '85vh',
                                                        overflowY: 'auto', overflowX: 'hidden', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Create Work Order</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 12 }}>
          {[
            ['wo_number', 'WO Number *', 'WO-2026-…'],
            ['part_name', 'Part Name', 'e.g. Bracket A12'],
            ['qty_ordered', 'Qty Ordered', '100'],
            ['due_date', 'Due Date', '', 'date'],
            ['customer_po', 'Customer PO', ''],
            ['end_customer', 'End Customer', ''],
          ].map(([k, label, ph, type]) => (
            <label key={k} style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 700, color: BR.dark }}>
              {label}
              <input type={type || 'text'} placeholder={ph} value={form[k]}
                     onChange={e => { const v = e.target.value; if (k === 'qty_ordered') { setForm(f => ({ ...f, qty_ordered: v })); recomputeAllOpTimes(parseInt(v, 10) || 0); } else { setForm(f => ({ ...f, [k]: v })); } }}
                     style={{ marginTop: 4, padding: '7px 9px', border: `1px solid ${BR.border}`, borderRadius: 5, fontSize: 13 }} />
            </label>
          ))}
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 700, color: BR.dark }}>
            Priority
            <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                    style={{ marginTop: 4, padding: '7px 9px', border: `1px solid ${BR.border}`, borderRadius: 5, fontSize: 13 }}>
              {['URGENT', 'HIGH', 'NORMAL', 'LOW'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
        </div>
        <div style={{ marginTop: 12, background: '#f8fafc', border: `1px solid ${BR.border}`, borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: BR.dark, marginBottom: 6 }}>Build route from Component</div>
          <ComponentCombo value={routeComp} components={components} placeholder="Pick a component to load its operations route..." onPick={code => { setRouteComp(code); loadRouteFromComponent(code); }} />
          <div style={{ fontSize: 11, color: BR.muted, marginTop: 6 }}>Loads this component operations and auto-generates the route card and followers when you create the WO.</div>
        </div>
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 800 }}>Operations (routing)</div>
            <button onClick={addOp} style={{ padding: '4px 10px', background: BR.blue, color: '#fff', border: 'none',
                                              borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>+ Add Op</button>
          </div>
          {ops.map((o, i) => {
            const _q = parseInt(form.qty_ordered, 10) || 0;
            const _p = perPartSec(o.component_number);
            const _mins = minsFor(o.component_number, _q);
            return (
            <React.Fragment key={i}>
            <div style={{ display: 'grid', gridTemplateColumns: '46px minmax(0, 1.9fr) minmax(0, 1.1fr) minmax(0, 1.2fr) 52px 20px',
                          gap: 6, marginBottom: 2, alignItems: 'start' }}>
              <input value={o.op_seq} onChange={e => updateOp(i, 'op_seq', e.target.value)} title="Op sequence"
                     style={{ height: 32, boxSizing: 'border-box', padding: 6, border: `1px solid ${BR.border}`, borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }} />
              <ComponentCombo value={o.component_number} components={components} placeholder="Component no or name…" onPick={code => pickOpComponent(i, code)} />
              <select value={o.work_center_shop_type || ''} onChange={e => {
                       const shop = e.target.value;
                       updateOp(i, 'work_center_shop_type', shop);
                       const firstWc = workCenters.find(w => w.shop_type === shop);
                       updateOp(i, 'work_center_code', firstWc?.code || '');
                       const stillValid = operationMaster.find(op => op.code === o.op_type && op.shop_type === shop);
                       if (!stillValid) { updateOp(i, 'op_name', ''); updateOp(i, 'op_type', ''); }
                     }}
                      style={{ height: 32, boxSizing: 'border-box', padding: 6, border: `1px solid ${BR.border}`, borderRadius: 4, fontSize: 12 }}>
                <option value="">— Shop —</option>
                {shopGroups.map(g => (
                  <option key={g.shop_type} value={g.shop_type}>{g.label}{g.opsPreview ? ` · ${g.opsPreview}` : ''}</option>
                ))}
              </select>
              <MultiOpPicker shop={o.work_center_shop_type} value={o.op_type} operationMaster={operationMaster}
                onChange={(codes, names) => { updateOp(i, 'op_type', codes.join(',')); updateOp(i, 'op_name', names.join(' + ')); }} />
              <input value={o.planned_time_min} placeholder="min" type="number" title="Planned minutes = (machining + handling) × qty"
                     onChange={e => updateOp(i, 'planned_time_min', e.target.value)}
                     style={{ height: 32, boxSizing: 'border-box', padding: 6, border: `1px solid ${BR.border}`, borderRadius: 4, fontSize: 12 }} />
              <button onClick={() => removeOp(i)} title="Remove" style={{ background: 'none', border: 'none', color: BR.red, cursor: 'pointer', fontSize: 16, height: 32 }}>×</button>
            </div>
            {o.component_number && (
              <div style={{ margin: '0 0 6px 52px', fontSize: 10, fontWeight: 600 }}>
                {_mins != null
                  ? <span style={{ color: BR.blue }}>⏱ machining {fmtSec(_p.mc)} + handling {fmtSec(_p.hd)} = {_p.mc + _p.hd}s/pc × {_q} = <b>{_mins} min</b></span>
                  : <span style={{ color: BR.amber }}>selected component has no machining / handling time</span>}
              </div>
            )}
            <SuggestedMachines op={o} machineCaps={machineCaps} />
            <RecipePicker op={o} recipeMaster={recipeMaster} value={o.recipe_code} onChange={code => updateOp(i, 'recipe_code', code)} />
            <SpecialProcessPicker op={o} spRecipes={spRecipes} value={o.special_process} onChange={code => updateOp(i, 'special_process', code)} />
            </React.Fragment>
          );})}
        </div>
        {err && <div style={{ color: BR.red, fontSize: 12, marginTop: 8, fontWeight: 700 }}>⚠ {err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#fff', color: BR.dark,
                                              border: `1px solid ${BR.border}`, borderRadius: 5, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding: '8px 18px', background: saving ? BR.muted : BR.orange,
                                                              color: '#fff', border: 'none', borderRadius: 5, cursor: saving ? 'wait' : 'pointer',
                                                              fontSize: 13, fontWeight: 700 }}>{saving ? 'Saving…' : 'Create WO'}</button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ wo, onClose, onSaved, customerId }) {
  const [form, setForm] = useState({
    part_number: wo.part_number || '', part_name: wo.part_name || '',
    qty_ordered: wo.qty_ordered || 0,
    due_date: wo.due_date ? new Date(wo.due_date).toISOString().slice(0, 10) : '',
    priority: wo.priority || 'NORMAL', customer_po: wo.customer_po || '',
    end_customer: wo.end_customer || '', notes: wo.notes || '',
  });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await updateWorkOrder(customerId, wo.id, { ...form, qty_ordered: parseInt(form.qty_ordered) || 0, due_date: form.due_date || null });
      onSaved();
    } catch (e) { setErr((e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Update failed'); setSaving(false); }
  };
  const FIELDS = [['part_number', 'Part Number'], ['part_name', 'Part Name'], ['qty_ordered', 'Qty Ordered', 'number'], ['due_date', 'Due Date', 'date'], ['customer_po', 'Customer PO'], ['end_customer', 'End Customer']];
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 540, maxHeight: '85vh', overflow: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 17, fontWeight: 800 }}>Edit {wo.wo_number}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>X</button>
        </div>
        {err && <div style={{ color: BR.red, marginBottom: 10, fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {FIELDS.map(([k, label, type]) => (
            <label key={k} style={{ fontSize: 12, fontWeight: 600, color: BR.dark }}>{label}
              <input type={type || 'text'} value={form[k]} onChange={e => set(k, e.target.value)}
                style={{ width: '100%', marginTop: 4, padding: '7px 9px', border: '1px solid ' + BR.border, borderRadius: 6, fontSize: 13 }} />
            </label>
          ))}
          <label style={{ fontSize: 12, fontWeight: 600, color: BR.dark }}>Priority
            <select value={form.priority} onChange={e => set('priority', e.target.value)} style={{ width: '100%', marginTop: 4, padding: '7px 9px', border: '1px solid ' + BR.border, borderRadius: 6, fontSize: 13 }}>
              {['URGENT', 'HIGH', 'NORMAL', 'LOW'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
        </div>
        <label style={{ fontSize: 12, fontWeight: 600, color: BR.dark, display: 'block', marginTop: 12 }}>Notes
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ width: '100%', marginTop: 4, padding: '7px 9px', border: '1px solid ' + BR.border, borderRadius: 6, fontSize: 13 }} />
        </label>
        <div style={{ fontSize: 11, color: BR.muted, marginTop: 8 }}>Changing Qty updates each operation planned quantity.</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#fff', color: BR.dark, border: '1px solid ' + BR.border, borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding: '8px 18px', background: saving ? BR.muted : BR.orange, color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

export default function WorkOrders() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [wos, setWos] = useState({ rows: [], count: 0 });
  const [workCenters, setWorkCenters] = useState([]);
  const [operationMaster, setOperationMaster] = useState([]);
  const [recipeMaster, setRecipeMaster] = useState([]);
  const [spRecipes, setSpRecipes] = useState([]);
  const [components, setComponents] = useState([]);
  const [machineCaps, setMachineCaps] = useState({});
  const [opSetupMaster, setOpSetupMaster] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [expandedOp, setExpandedOp] = useState(null);
  const [expandedDetailOp, setExpandedDetailOp] = useState(null);
  const [detailSessions, setDetailSessions] = useState({});
  const [batchLog, setBatchLog] = useState({}); // { [op_id]: { [batch_n]: {clock_in, clock_out} } }
  const [detailFull, setDetailFull] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [reportRows, setReportRows] = useState([]);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportPage, setReportPage] = useState(1);
  const [procData, setProcData] = useState(null);
  const [currentOps, setCurrentOps] = useState({});
  const [resetBusy, setResetBusy] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [openBatch, setOpenBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editWo, setEditWo] = useState(null);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);

  const [erpBusy, setErpBusy] = useState(false);
  async function erpSync() {
    if (erpBusy) return;
    setErpBusy(true);
    try {
      // Demo: ERP Sync just refreshes the list (no mock Epicor WOs pulled in).
      await refresh();
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
                   icon: 'success', title: 'ERP sync complete' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'ERP sync failed',
                   text: e?.response?.data?.message || e.message || 'Unknown error',
                   confirmButtonColor: '#f97316' });
    } finally {
      setErpBusy(false);
    }
  }

  const loadReport = async () => {
    setReportBusy(true);
    try {
      const list = await listWorkOrders(customerId, { limit: 200 });
      const rows = Array.isArray(list) ? list : (list.rows || list.data || []);
      const details = await Promise.all(rows.map(w => getWorkOrderDetail(customerId, w.id).catch(() => null)));
      const flat = [];
      details.forEach((d, i) => { if (!d) return; const wo = d.wo || rows[i];
        (d.operations || []).forEach(op => {
          if (op.work_center_shop_type === 'SPECIAL_PROCESS') {
            const q = Number(op.planned_qty)||0, jig = 20, nb = Math.max(1, Math.ceil(q/jig)); const dn = op.status==='DONE'||op.status==='CLAIMED';
            for (let b = 0; b < nb; b++) { const bq = Math.min(jig, q - b*jig); const bst = dn ? 'DONE' : (op.status==='IN_PROGRESS' ? (b===0?'IN_PROGRESS':'PENDING') : 'PENDING');
              flat.push({ wo, op: { ...op, op_seq: op.op_seq+'_'+((b+1)*10), planned_qty: bq, actual_qty_good: dn?bq:(bst==='IN_PROGRESS'?Math.floor(bq*0.6):0), actual_qty_scrap: 0, status: bst, __batchIdx: b } }); }
          } else { flat.push({ wo, op }); }
        });
      });
      flat.sort((a, b) => a.wo.wo_number === b.wo.wo_number ? ((a.op.op_seq_int||0)-(b.op.op_seq_int||0)) : String(a.wo.wo_number).localeCompare(String(b.wo.wo_number)));
      setReportRows(flat); setReportPage(1);
    } catch (e) { setErr(e && e.message ? e.message : String(e)); }
    setReportBusy(false);
  };
  const refresh = useCallback(async () => {
    if (!customerId) { setErr('No customer_id available'); setLoading(false); return; }
    try {
      const [list, wcs, opAttr, capAttr, recAttr, compAttr, spAttr, setupAttr] = await Promise.all([
        listWorkOrders(customerId, { status: statusFilter || undefined, q: q || undefined, limit: 200 }),
        listWorkCenters(customerId),
        customerbasedshift(customerId, 'operation_master').catch(() => []),
        customerbasedshift(customerId, 'machine_capabilities').catch(() => []),
        customerbasedshift(customerId, 'recipe_master').catch(() => []),
        customerbasedshift(customerId, 'component').catch(() => []),
        customerbasedshift(customerId, 'sp_recipes').catch(() => []),
        customerbasedshift(customerId, 'op_setup_master').catch(() => []),
      ]);
      setWos(list); setWorkCenters(wcs);
      setOperationMaster(Array.isArray(opAttr?.[0]?.value) ? opAttr[0].value : []);
      setMachineCaps(capAttr?.[0]?.value && typeof capAttr[0].value === 'object' ? capAttr[0].value : {});
      setRecipeMaster(Array.isArray(recAttr?.[0]?.value) ? recAttr[0].value : []);
      setComponents(Array.isArray(compAttr?.[0]?.value) ? compAttr[0].value : []);
      setSpRecipes(Array.isArray(spAttr?.[0]?.value) ? spAttr[0].value : []);
      setOpSetupMaster(Array.isArray(setupAttr?.[0]?.value) ? setupAttr[0].value : []);
      setErr(null);
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
    finally { setLoading(false); }
  }, [customerId, statusFilter, q]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!selected?.id) { setDetail(null); setDetailSessions({}); setExpandedDetailOp(null); return; }
    getWorkOrderDetail(customerId, selected.id).then(setDetail).catch(e => setErr(e?.message));
    (async () => {
      try {
        const base = `${window.location.protocol}//${window.location.hostname}:6017/demo-reset`;
        const r = await fetch(`${base}/sessions?wo=${encodeURIComponent(selected.wo_number)}`);
        const j = await r.json();
        const g = {}; (Array.isArray(j) ? j : []).forEach(x => { (g[x.op_seq] = g[x.op_seq] || []).push(x); });
        setDetailSessions(g);
      } catch (e) { setDetailSessions({}); }
      try {
        const base = `${window.location.protocol}//${window.location.hostname}:6017/demo-reset`;
        const r = await fetch(`${base}/batch-times?wo=${encodeURIComponent(selected.wo_number)}`);
        const j = await r.json();
        const bl = {}; (Array.isArray(j) ? j : []).forEach(x => { (bl[x.op_id] = bl[x.op_id] || {})[x.batch_n] = { clock_in: x.clock_in, clock_out: x.clock_out }; });
        setBatchLog(bl);
      } catch (e) { setBatchLog({}); }
    })();
  }, [selected, customerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rows = wos.rows || [];
      const map = {};
      await Promise.all(rows.map(async (w) => {
        try { const d = await getWorkOrderDetail(customerId, w.id); const ops = (d.operations || []).slice().sort((a, b) => (a.op_seq_int||0) - (b.op_seq_int||0)); const cur = ops.find(o => o.status !== 'DONE' && o.status !== 'CLAIMED'); map[w.id] = { seq: cur?cur.op_seq:null, name: cur?cur.op_name:null, ops: ops.map(o=>({ seq:o.op_seq, status:o.status })) }; } catch (e) {}
      }));
      if (!cancelled) setCurrentOps(map);
    })();
    return () => { cancelled = true; };
  }, [wos, customerId]);

  useEffect(() => {
    if (typeof document === 'undefined' || document.getElementById('wo-pulse-style')) return;
    const el = document.createElement('style'); el.id = 'wo-pulse-style';
    el.textContent = '@keyframes wopulse { 0%,100%{ box-shadow:0 0 0 0 rgba(249,115,22,0.6);} 50%{ box-shadow:0 0 0 7px rgba(249,115,22,0);} }';
    document.head.appendChild(el);
  }, []);

  useEffect(() => {
    const id = setInterval(() => { refresh(); }, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  const doReset = async () => {
    if (!resetConfirm) { setResetConfirm(true); setTimeout(() => setResetConfirm(false), 4000); return; }
    setResetConfirm(false); setResetBusy(true);
    try {
      await fetch(`${window.location.protocol}//${window.location.hostname}:6017/demo-reset/reset`, { method: 'POST' });
      setSelected(null); setDetail(null);
      await refresh();
    } catch (e) { setErr('Reset failed: ' + (e?.message || e)); }
    setResetBusy(false);
  };

  async function handleDelete(w) {
    const conf = await Swal.fire({ title: 'Delete ' + w.wo_number + '?', text: 'This permanently removes the work order and its operations.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes, delete', confirmButtonColor: '#dc2626' });
    if (!conf.isConfirmed) return;
    try {
      await deleteWorkOrder(customerId, w.id);
      if (selected && selected.id === w.id) { setSelected(null); setDetail(null); }
      setInfo('Deleted ' + w.wo_number); setTimeout(() => setInfo(null), 3000);
      refresh();
    } catch (e) { setErr((e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Delete failed'); }
  }

  async function handleRelease(wo_id) {
    try {
      await releaseWorkOrder(customerId, wo_id);
      setInfo('Released — first operation now in operator queue');
      setTimeout(() => setInfo(null), 3000);
      refresh();
      if (selected?.id === wo_id) {
        const d = await getWorkOrderDetail(customerId, wo_id);
        setDetail(d);
      }
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
  }

  async function auditPackage(wo_id) {
    try {
      const base = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
      // Fetch WO detail (ops + claims), NC log, calibration register and op-quals in parallel
      const [woResp, ncResp, calResp, qualResp] = await Promise.all([
        axios.get(`${base}/api/workorder/detail/${wo_id}`, { params: { customer_id: customerId } }),
        axios.get(`${base}/api/nc-log`, { params: { customer_id: customerId } }).catch(() => ({ data: [] })),
        customerbasedshift(customerId, 'calibration_register').catch(() => []),
        customerbasedshift(customerId, 'operator_qualifications').catch(() => []),
      ]);
      const d = woResp.data || {};
      const wo = d.wo || {};
      const ops = d.operations || [];
      const nc = (ncResp.data || []).filter(n => n.wo_number === wo.wo_number);
      const cal = Array.isArray(calResp?.[0]?.value) ? calResp[0].value : [];
      const quals = Array.isArray(qualResp?.[0]?.value) ? qualResp[0].value : [];
      const today = new Date();
      const certStatus = (opName) => {
        if (!opName) return null;
        const q = quals.find(qq => (qq.operator_name || '').toLowerCase() === opName.toLowerCase());
        if (!q || !q.expiry_date) return null;
        const days = Math.round((new Date(q.expiry_date) - today) / 86400000);
        return { cert: q.certification, expires: q.expiry_date, days, status: days < 0 ? 'EXPIRED' : days <= 30 ? 'EXPIRING' : 'VALID' };
      };
      const calOverdue = cal.filter(c => c.next_cal_due_date && new Date(c.next_cal_due_date) < today).length;
      const calWarning = cal.filter(c => c.next_cal_due_date && new Date(c.next_cal_due_date) >= today && (new Date(c.next_cal_due_date) - today)/86400000 <= 30).length;
      const html = `
<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>Audit Package ${wo.wo_number}</title>
<style>
  * { box-sizing: border-box; font-family: Arial, sans-serif; }
  body { margin: 22px; color: #111; }
  .h { display: flex; justify-content: space-between; border-bottom: 3px solid #f97316; padding-bottom: 10px; margin-bottom: 18px; }
  h1 { margin: 0; font-size: 24px; color: #f97316; }
  .wo { font-size: 20px; font-weight: 800; margin: 4px 0; }
  .sub { font-size: 12px; color: #6b7280; }
  .qr-block { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 6px; border: 1px solid #e5e7eb; border-radius: 4px; }
  .qr-block img { width: 90px; height: 90px; }
  h2 { margin: 20px 0 8px; font-size: 14px; padding: 6px 10px; background: #fff7ed; color: #9a3412; border-left: 4px solid #f97316; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px; }
  th { background: #f3f4f6; color: #374151; padding: 6px 8px; text-align: left; font-size: 10px; font-weight: 800; text-transform: uppercase; border-bottom: 1px solid #d1d5db; }
  td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  .badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 9px; font-weight: 800; }
  .b-ok { background: #dcfce7; color: #166534; border: 1px solid #16a34a; }
  .b-warn { background: #fef3c7; color: #92400e; border: 1px solid #f59e0b; }
  .b-bad { background: #fee2e2; color: #991b1b; border: 1px solid #dc2626; }
  .meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; font-size: 11px; }
  .meta div { background: #f9fafb; padding: 7px 9px; border: 1px solid #e5e7eb; border-radius: 4px; }
  .meta strong { display: block; font-size: 9px; color: #6b7280; text-transform: uppercase; }
  .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #d1d5db; font-size: 10px; color: #6b7280; }
  .no-print { background: #f97316; color: #fff; padding: 10px 18px; border: none; border-radius: 4px; font-weight: 700; cursor: pointer; margin-bottom: 14px; }
  @media print { body { margin: 12px; } .no-print { display: none; } }
  .empty { padding: 10px; color: #6b7280; font-size: 11px; font-style: italic; }
</style></head><body>
<button class="no-print" onclick="window.print()">🖨 Print Audit Package</button>
<div class="h">
  <div>
    <h1>AUDIT PACKAGE \u00b7 NADCAP-READY</h1>
    <div class="wo">${wo.wo_number}</div>
    <div class="sub">${wo.part_name || ''} \u00b7 ${wo.part_number || ''} \u00b7 Qty ${wo.qty_ordered}</div>
  </div>
  <div class="qr-block">
    <img src="https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent(wo.wo_number)}"/>
    <div style="font-family:monospace;font-size:10px;color:#6b7280;">${wo.wo_number}</div>
  </div>
</div>

<h2>1 \u00b7 Work Order Summary</h2>
<div class="meta">
  <div><strong>Status</strong>${wo.status}</div>
  <div><strong>Priority</strong>${wo.priority || 'NORMAL'}</div>
  <div><strong>Source</strong>${wo.source || 'MANUAL'}</div>
  <div><strong>Customer PO</strong>${wo.customer_po || '-'}</div>
  <div><strong>End Customer</strong>${wo.end_customer || '-'}</div>
  <div><strong>Due Date</strong>${wo.due_date ? new Date(wo.due_date).toLocaleDateString('en-IN') : '-'}</div>
  <div><strong>Released</strong>${wo.released_ts ? new Date(wo.released_ts).toLocaleDateString('en-IN') : '-'}</div>
  <div><strong>Completed</strong>${wo.completed_ts ? new Date(wo.completed_ts).toLocaleDateString('en-IN') : '-'}</div>
</div>

<h2>2 \u00b7 Operations Genealogy</h2>
<table>
  <thead><tr><th>Op</th><th>Operation</th><th>Process</th><th>Machine</th><th>Operator</th><th>Cert Status</th><th>Good</th><th>Scrap</th><th>Time</th><th>Claimed at</th><th>Status</th></tr></thead>
  <tbody>
${ops.map(o => {
  const cs = certStatus(o.claimed_by_operator_name);
  const csB = !cs ? '<span class="badge b-warn">no cert</span>' : cs.status === 'VALID' ? `<span class="badge b-ok">${cs.cert ? cs.cert.split(' \u2014')[0] : 'VALID'}</span>` : cs.status === 'EXPIRING' ? `<span class="badge b-warn">EXPIRES ${cs.days}d</span>` : `<span class="badge b-bad">EXPIRED ${Math.abs(cs.days)}d</span>`;
  const statusB = ['CLAIMED','DONE'].includes(o.status) ? '<span class="badge b-ok">COMPLETED</span>' : `<span class="badge b-warn">${o.status}</span>`;
  return `<tr><td><strong>${o.op_seq}</strong></td><td><strong>${o.op_name || ''}</strong></td><td>${o.work_center_shop_type || ''}</td><td>${o.machine_name || '-'}</td><td>${o.claimed_by_operator_name || '-'}</td><td>${csB}</td><td>${o.actual_qty_good || 0}</td><td>${o.actual_qty_scrap || 0}</td><td>${o.actual_time_min ? o.actual_time_min + 'm' : '-'}</td><td>${o.claimed_at ? new Date(o.claimed_at).toLocaleString('en-IN') : '-'}</td><td>${statusB}</td></tr>`;
}).join('')}
  </tbody>
</table>

<h2>3 \u00b7 Calibration Snapshot</h2>
${(calOverdue + calWarning) === 0 ? `<div class="empty">All ${cal.length} registered instruments are within calibration. No findings.</div>` : `
<div style="margin-bottom:6px;font-size:11px;">
  <span class="badge b-bad">${calOverdue} OVERDUE</span> \u00b7
  <span class="badge b-warn">${calWarning} DUE \u2264 30 DAYS</span> \u00b7
  ${cal.length - calOverdue - calWarning} valid (of ${cal.length} total)
</div>
<table><thead><tr><th>Instrument</th><th>Type</th><th>Location</th><th>Last Cal</th><th>Next Due</th><th>Status</th></tr></thead><tbody>
${cal.filter(c => { const due = new Date(c.next_cal_due_date); return c.next_cal_due_date && (due < today || (due - today)/86400000 <= 30); }).map(c => {
  const due = new Date(c.next_cal_due_date);
  const days = Math.round((due - today)/86400000);
  const b = days < 0 ? `<span class="badge b-bad">OVERDUE ${Math.abs(days)}d</span>` : `<span class="badge b-warn">DUE ${days}d</span>`;
  return `<tr><td><strong>${c.instrument_id}</strong></td><td>${c.instrument_type || '-'}</td><td>${c.location || '-'}</td><td>${c.last_cal_date || '-'}</td><td>${c.next_cal_due_date}</td><td>${b}</td></tr>`;
}).join('')}
</tbody></table>`}

<h2>4 \u00b7 Non-Conformance Records</h2>
${nc.length === 0 ? `<div class="empty">No NC records linked to ${wo.wo_number}.</div>` : `
<table><thead><tr><th>NC #</th><th>Op</th><th>Defect</th><th>Severity</th><th>Raised</th><th>Status</th></tr></thead><tbody>
${nc.map(n => `<tr><td><strong>${n.nc_number || n.id}</strong></td><td>${n.op_seq || '-'}</td><td>${n.defect_type || n.description || '-'}</td><td>${n.severity || '-'}</td><td>${n.created_ts ? new Date(n.created_ts).toLocaleString('en-IN') : '-'}</td><td>${n.status || '-'}</td></tr>`).join('')}
</tbody></table>`}

<h2>5 \u00b7 Sign-off Block</h2>
<div class="meta">
  <div><strong>Production Supervisor</strong><br/><br/>______________________<br/><span class="sub">Name &amp; date</span></div>
  <div><strong>QA Sign-off</strong><br/><br/>______________________<br/><span class="sub">Name &amp; date</span></div>
  <div><strong>Material Cert Attached</strong><br/><br/>\u2610 Yes &nbsp; \u2610 N/A</div>
  <div><strong>Customer Flow-down Verified</strong><br/><br/>\u2610 Yes &nbsp; \u2610 N/A</div>
</div>

<div class="footer">
  Generated by Yantra Smart Shop Floor MES \u00b7 ${new Date().toLocaleString('en-IN')} \u00b7
  Audit Package for ${wo.wo_number} \u00b7 ALCOA-aligned record \u00b7
  Retain per customer flow-down spec (typ. 7\u201310 yrs for aerospace).
</div>
</body></html>`;
      const w = window.open('', '_blank');
      if (w) { w.document.write(html); w.document.close(); }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Audit package failed', text: e?.response?.data?.message || e.message, confirmButtonColor: '#f97316' });
    }
  }

  async function printTraveler(wo_id) {
    try {
      const axios = (await import('axios')).default;
      const r = await axios.get(`${window._env_.SERVER_URL2}api/workorder/traveler/${wo_id}`,
                                 { params: { customer_id: customerId } });
      const d = r.data;
      const wo = d.wo;
      const ops = d.operations;
      const win = window.open('', '_blank', 'width=900,height=1100');
      if (!win) { setErr('Pop-up blocked — allow pop-ups to print traveler'); return; }
      const html = `
<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><title>Traveler ${wo.wo_number}</title>
<style>
  * { box-sizing: border-box; font-family: Arial, sans-serif; }
  body { margin: 24px; color: #111; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #f97316; padding-bottom: 10px; margin-bottom: 14px; }
  .h-left h1 { margin: 0 0 4px 0; font-size: 26px; color: #f97316; }
  .h-left .wo { font-size: 22px; font-weight: 800; margin: 4px 0; }
  .h-left .part { font-size: 14px; color: #555; }
  .h-right { text-align: right; font-size: 11px; color: #555; }
  .h-right .qty { font-size: 22px; font-weight: 900; color: #111; }
  .meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 10px 0 18px; font-size: 12px; }
  .meta div { padding: 8px 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; }
  .meta strong { display: block; font-size: 10px; color: #6b7280; text-transform: uppercase; margin-bottom: 2px; letter-spacing: 0.5px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 6px; }
  th { background: #f97316; color: #fff; padding: 8px 10px; text-align: left; font-size: 11px; }
  td { padding: 8px 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  td.seq { font-family: monospace; font-weight: 800; }
  td.signoff { width: 130px; }
  .signoff-box { border: 1.5px dashed #6b7280; height: 28px; margin-top: 2px; border-radius: 3px; }
  .qr-block { display: inline-flex; flex-direction: column; align-items: center; gap: 4px;
              margin-top: 8px; padding: 6px; background: #fff; border: 1px solid #e5e7eb; border-radius: 4px; }
  .qr-block img { display: block; width: 96px; height: 96px; }
  .qr-block .qr-cap { font-size: 9px; color: #6b7280; font-family: monospace; letter-spacing: 0.4px; }
  .footer { margin-top: 24px; display: flex; justify-content: space-between; font-size: 11px; color: #6b7280; }
  @media print { body { margin: 12px; } .no-print { display: none; } }
  .no-print { background: #f97316; color: #fff; padding: 10px 18px; border: none; border-radius: 4px;
              font-weight: 700; cursor: pointer; margin-bottom: 14px; }
</style></head><body>
<button class="no-print" onclick="window.print()">🖨 Print</button>
<div class="header">
  <div class="h-left">
    <h1>WORK ORDER TRAVELER / ROUTE CARD</h1>
    <div class="wo">${wo.wo_number}</div>
    <div class="part"><strong>${wo.part_name || '-'}</strong> &nbsp;––– ${wo.part_number || '-'}</div>
  </div>
  <div class="h-right">
    <div class="qty">${wo.qty_ordered} pcs</div>
    Status: <strong>${wo.status}</strong><br>
    <div class="qr-block">
      <img src="https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=0&data=${encodeURIComponent(wo.wo_number)}"
           alt="QR ${wo.wo_number}" />
      <div class="qr-cap">${wo.wo_number}</div>
    </div>
    Priority: <strong>${wo.priority || 'NORMAL'}</strong><br>
    Source: <strong>${wo.source}</strong>
  </div>
</div>

<div class="meta">
  <div><strong>Customer PO</strong> ${wo.customer_po || '-'}</div>
  <div><strong>End Customer</strong> ${wo.end_customer || '-'}</div>
  <div><strong>Due Date</strong> ${wo.due_date ? new Date(wo.due_date).toLocaleDateString('en-IN') : '-'}</div>
  <div><strong>Released At</strong> ${wo.released_ts ? new Date(wo.released_ts).toLocaleDateString('en-IN') : '-'}</div>
  <div><strong>Total Planned</strong> ${Number(d.summary.total_planned_min || 0).toFixed(1)} min</div>
  <div><strong>Operations</strong> ${d.summary.total_ops}</div>
  <div><strong>Done</strong> ${d.summary.done_ops}</div>
  <div><strong>Pending</strong> ${d.summary.pending_ops}</div>
</div>

<table>
  <thead><tr>
    <th>Op</th><th>Operation</th><th>Work Center</th><th>Machine</th>
    <th>Plan Time</th><th>Plan Qty</th><th>Good</th><th>Scrap</th>
    <th>Status</th><th>Operator</th><th>Sign-off</th>
  </tr></thead>
  <tbody>
${ops.map(o => `    <tr>
      <td class="seq">${o.op_seq}</td>
      <td><strong>${o.op_name}</strong><br><span style="font-size:10px;color:#888;">${o.work_center_shop_type || ''}</span></td>
      <td>${o.work_center_code || '-'}</td>
      <td>${o.machine_name || '-'}</td>
      <td>${o.planned_time_min || 0} min</td>
      <td>${o.planned_qty || 0}</td>
      <td>${o.actual_qty_good || ''}</td>
      <td>${o.actual_qty_scrap || ''}</td>
      <td><strong>${o.status}</strong></td>
      <td>${o.claimed_by_operator_name || ''}</td>
      <td class="signoff"><div class="signoff-box"></div></td>
    </tr>`).join('')}
  </tbody>
</table>

<div class="footer">
  <div>Yantra Smart Shop Floor MES &middot; Generated ${new Date(d.generated_at).toLocaleString('en-IN')}</div>
  <div>Traveler ${wo.wo_number}</div>
</div>
</body></html>`;
      win.document.write(html);
      win.document.close();
    } catch (e) {
      setErr(e?.response?.data?.message || e.message);
    }
  }

  async function handleSeed() {
    try {
      const r = await seedDemo(customerId);
      setInfo(`Seed: ${r.status} (${r.work_centers || 0} work centers, ${r.work_orders || 0} WOs, ${r.released || 0} released)`);
      setTimeout(() => setInfo(null), 5000);
      refresh();
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
  }

  const shopColorMap = useMemo(() => ({
    MACHINE_SHOP: '#2563eb', ASSEMBLY: '#16a34a', DEBURRING: '#f59e0b',
    SPECIAL_PROCESS: '#dc2626', INSPECTION: '#9333ea', PACKING: '#6b7280', OTHER: '#9ca3af',
  }), []);

  return (
    <div style={{ padding: 18, background: BR.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: BR.dark }}>Work Order List</div>
          <div style={{ fontSize: 11, color: BR.muted }}>
            Plan · Release · Track · Claim — operations route across {new Set(workCenters.map(w => w.shop_type)).size} shops
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {/* + Create WO hidden for demo; reveal with ?demo=1 for testing */}
          {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') && (
            <button onClick={() => setCreateOpen(true)} style={{ padding: '8px 16px', background: BR.orange, color: '#fff',
                                                    border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              + Create WO
            </button>
          )}
          {wos.count === 0 && (
            <button onClick={handleSeed} style={{ padding: '8px 16px', background: BR.blue, color: '#fff',
                                                    border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              ⚡ Seed Demo Data
            </button>
          )}
          {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') && (
            <button onClick={doReset} disabled={resetBusy}
                    title="Restore all demo work orders to their initial state (hidden from customers)"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                              background: resetConfirm ? BR.red : '#fff', color: resetConfirm ? '#fff' : BR.dark,
                              border: `1px solid ${resetConfirm ? BR.red : BR.border}`, borderRadius: 6,
                              fontWeight: 700, fontSize: 13, cursor: resetBusy ? 'wait' : 'pointer' }}>
              ↺ {resetBusy ? 'Resetting…' : resetConfirm ? 'Click again to confirm' : 'Reset Demo'}
            </button>
          )}
          <button onClick={erpSync} disabled={erpBusy}
                  title="Pull work orders from ERP, then refresh"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '8px 14px', background: erpBusy ? BR.muted : BR.blue, color: '#fff',
                            border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13,
                            cursor: erpBusy ? 'wait' : 'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            {erpBusy ? 'Syncing…' : 'ERP Sync'}
          </button>
        </div>
      </div>

      {info && (
        <div style={{ padding: 10, background: '#dcfce7', border: `1px solid ${BR.green}`, borderRadius: 5,
                      color: '#14532d', fontSize: 13, marginBottom: 10, fontWeight: 700 }}>{info}</div>
      )}
      {err && (
        <div style={{ padding: 10, background: '#fee2e2', border: `1px solid ${BR.red}`, borderRadius: 5,
                      color: '#7a0000', fontSize: 13, marginBottom: 10 }}>⚠ {err}</div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {['', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETE'].map(s => (
          <button key={s || 'all'} onClick={() => setStatusFilter(s)}
                  style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700,
                            background: statusFilter === s ? BR.dark : '#fff',
                            color: statusFilter === s ? '#fff' : BR.dark,
                            border: `1px solid ${BR.border}`, borderRadius: 4, cursor: 'pointer' }}>
            {s ? (STATUS_LABEL[s] || s) : 'ALL'}
          </button>
        ))}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search WO/part…"
               style={{ marginLeft: 8, padding: '6px 10px', border: `1px solid ${BR.border}`, borderRadius: 4, fontSize: 13, minWidth: 200 }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
        {/* WO List */}
        {viewMode === 'list' && (
        <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: BR.muted }}>Loading…</div>
          ) : wos.rows.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
              No work orders. Click <strong>Seed Demo Data</strong> to start with PMI sample data.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
              <thead>
                <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                  {['WO #', 'Part', 'Current Process', 'Qty', 'Status', ''].map((h, i) => (
                    <th key={h} style={{ padding: h==='Qty' ? '11px 24px 11px 12px' : '11px 12px', textAlign: h==='Qty' ? 'right' : (h==='Prio'||h==='Status') ? 'center' : 'left', fontSize: 12, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {wos.rows.map(w => (
                  <tr key={w.id}
                      onClick={() => setSelected(w)}
                      style={{ borderBottom: `1px solid ${BR.border}`, cursor: 'pointer',
                                background: selected?.id === w.id ? '#fff7ed' : 'transparent' }}>
                    <td style={{ padding: '11px 12px', fontFamily: 'monospace', fontWeight: 700, fontSize: 16 }}>{w.wo_number}</td>
                    <td style={{ padding: '11px 12px', fontSize: 15 }}>
                      <div style={{ fontWeight: 700 }}>{w.part_name || '—'}</div>
                      <div style={{ color: BR.muted, fontSize: 13 }}>{w.part_number || ''}</div>
                    </td>
                    <td style={{ padding: '11px 12px', fontSize: 15 }}>
                      {(() => {
                        const info = currentOps[w.id];
                        if (!info || !info.ops || !info.ops.length) return (<span style={{ color: BR.muted }}>—</span>);
                        const curIdx = info.ops.findIndex(o => o.status !== 'DONE' && o.status !== 'CLAIMED');
                        return (<div>
                          <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', padding: '2px 0' }}>
                            {info.ops.map((o, i) => { const done = o.status==='DONE'||o.status==='CLAIMED'; const cur = i===curIdx; const prevDone = i>0 && (info.ops[i-1].status==='DONE'||info.ops[i-1].status==='CLAIMED'); const nb = done ? '#16a34a' : (cur ? '#f97316' : '#cbd5e1'); const nbg = done ? '#16a34a' : (cur ? '#f97316' : '#fff'); const nc = (done||cur) ? '#fff' : '#94a3b8'; const num = (o.seq||'').replace(/^OP/i,''); return (
                              <div key={i} title={o.seq} style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                                {i>0 && <div style={{ width: 20, height: 3, borderRadius: 2, background: prevDone ? '#16a34a' : '#e2e8f0' }} />}
                                <div style={{ width: 30, height: 30, borderRadius: '50%', background: nbg, border: `2px solid ${nb}`, color: nc, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, animation: cur ? 'wopulse 1.4s infinite' : 'none' }}>{num}</div>
                              </div>); })}
                          </div>
                          <div style={{ marginTop: 6, fontSize: 14.5, fontWeight: 700 }}>{info.name ? <span style={{ color: '#f97316' }}>▶ {info.seq} · {info.name}</span> : <span style={{ color: BR.green }}>✓ All operations completed</span>}</div>
                        </div>);
                      })()}
                    </td>
                    <td style={{ padding: '9px 24px 9px 12px', textAlign: 'right', fontWeight: 700, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      <span style={{ color: w.qty_completed >= w.qty_ordered ? BR.green : BR.dark }}>
                        {w.qty_completed || 0}
                      </span>
                      <span style={{ color: BR.muted }}> / {w.qty_ordered}</span>
                      {w.qty_scrap > 0 && (
                        <div style={{ fontSize: 10, color: BR.red, fontWeight: 700 }}>↻ {w.qty_scrap} scrap</div>
                      )}
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'center' }}><Pill>{w.status}</Pill></td>
                    <td style={{ padding: '9px 12px' }}>
                      {w.status === 'DRAFT' && (
                        <button onClick={(e) => { e.stopPropagation(); handleRelease(w.id); }}
                                style={{ padding: '4px 10px', background: BR.green, color: '#fff',
                                          border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          Release ▶
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}
        {viewMode === 'report' && (
        <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'auto' }}>
          {reportBusy ? (<div style={{ padding: 30, textAlign: 'center', color: BR.muted }}>Loading report…</div>) : reportRows.length === 0 ? (<div style={{ padding: 30, textAlign: 'center', color: BR.muted, fontSize: 13 }}>No operations to report.</div>) : (() => {
            const PER = 12; const pages = Math.max(1, Math.ceil(reportRows.length / PER)); const pg = Math.min(reportPage, pages);
            const slice = reportRows.slice((pg-1)*PER, pg*PER);
            const cellS = { padding: '8px 10px', color: BR.dark, whiteSpace: 'nowrap' };
            const pgBtn = { padding: '6px 12px', border: `1px solid ${BR.border}`, borderRadius: 5, background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 };
            const fmtD = (t) => { if (!t) return '—'; const d = new Date(t); return String(d.getDate()).padStart(2,'0')+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+d.getFullYear(); };
            const fmtT = (t) => t ? new Date(t).toTimeString().slice(0,8) : '—';
            const shiftOf = (t) => { if (!t) return '—'; const h = new Date(t).getHours(); return h>=6&&h<14 ? 'Shift 1 · 06:00-14:00' : (h>=14&&h<22 ? 'Shift 2 · 14:00-22:00' : 'Shift 3 · 22:00-06:00'); };
            const cols = ['Date','Route Card','Part No.','Operation','Machine','Operator','Shift','Order Qty','Actual','NC Qty','NC No','Available','Clock In','Clock Out','Status','Process Data'];
            return (<div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                  {cols.map(c => (<th key={c} style={{ padding: '8px 10px', textAlign: 'left', color: BR.muted, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{c}</th>))}
                </tr></thead>
                <tbody>
                  {slice.map((r, ri) => { const op = r.op, wo = r.wo; const good = Number(op.actual_qty_good)||0, scrap = Number(op.actual_qty_scrap)||0, rework = Number(op.actual_qty_rework)||0, ncp = Number(op.actual_qty_nc)||0; const sp = op.work_center_shop_type === 'SPECIAL_PROCESS';
                    return (<tr key={ri} style={{ borderBottom: `1px solid ${BR.border}` }}>
                      <td style={cellS}>{fmtD(op.started_at || op.created_ts)}</td>
                      <td style={{...cellS, fontWeight: 700}}>{wo.wo_number}</td>
                      <td style={cellS}>{wo.part_number}</td>
                      <td style={{...cellS, fontWeight: 700}}>{op.op_seq}</td>
                      <td style={cellS}>{op.machine_name || '—'}</td>
                      <td style={cellS}>{op.claimed_by_operator_name || '—'}</td>
                      <td style={cellS}>{shiftOf(op.started_at)}</td>
                      <td style={cellS}>{op.status==='PENDING' ? '—' : (op.planned_qty!=null ? op.planned_qty : '—')}</td>
                      <td style={cellS}>{good+ncp+scrap+rework}</td>
                      <td style={cellS}>{ncp+scrap+rework}</td>
                      <td style={cellS}>{op.scrap_reason || '—'}</td>
                      <td style={{...cellS, fontWeight: 700}}>{good}</td>
                      <td style={cellS}>{fmtT(op.started_at)}</td>
                      <td style={cellS}>{(op.status==='DONE'||op.status==='CLAIMED') ? fmtT(op.claimed_at) : '—'}</td>
                      <td style={cellS}><Pill>{op.status}</Pill></td>
                      <td style={cellS}>{(sp && (op.status === 'DONE' || op.status === 'CLAIMED' || op.status === 'IN_PROGRESS')) ? <button onClick={() => setProcData({ op, wo: (typeof wo !== 'undefined' ? wo : detail.wo), batch: (op.__batchIdx != null ? op.__batchIdx : null) })} style={{ padding: '4px 8px', background: BR.blue, color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>Process Data</button> : '—'}</td>
                    </tr>); })}
                </tbody>
              </table>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 12 }}>
                <button onClick={() => setReportPage(Math.max(1, pg-1))} disabled={pg<=1} style={pgBtn}>Prev</button>
                <span style={{ fontSize: 12, color: BR.muted }}>Page {pg} / {pages} · {reportRows.length} ops</span>
                <button onClick={() => setReportPage(Math.min(pages, pg+1))} disabled={pg>=pages} style={pgBtn}>Next</button>
              </div>
            </div>);
          })()}
        </div>
        )}

        {/* WO Detail */}
        {selected && detail && (
          <div onClick={() => { setSelected(null); setDetail(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 10, padding: 20, maxWidth: '96vw', width: '96vw', maxHeight: '95vh', height: '95vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: BR.dark, fontFamily: 'monospace' }}>{detail.wo.wo_number}</div>
                <div style={{ fontSize: 13, color: BR.dark, marginTop: 2 }}>{detail.wo.part_name} · <span style={{ color: BR.muted }}>{detail.wo.part_number}</span></div>
                <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 12, color: BR.muted }}>
                  <span>Qty: <strong style={{ color: BR.dark }}>{detail.wo.qty_completed || 0} / {detail.wo.qty_ordered}</strong></span>
                  <span>Scrap: <strong style={{ color: detail.wo.qty_scrap > 0 ? BR.red : BR.dark }}>{detail.wo.qty_scrap || 0}</strong></span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                <Pill>{detail.wo.status}</Pill>
                <button onClick={() => { setSelected(null); setDetail(null); }} style={{ background: 'none', border: 'none', color: BR.muted, cursor: 'pointer', fontSize: 18 }}>×</button>
              </div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', marginBottom: 6 }}>
              Operations Routing
            </div>
            <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Op','Operation','Machine','Operator','Shift','Order Qty','Actual','NC Qty','NC No','Available','Rework','Scrap','Clock In','Clock Out','Setup Time','Runtime','Duration','Status','Process Data'].map(c => (<th key={c} style={{ padding: '8px 10px', textAlign: 'left', color: BR.muted, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{c}</th>))}
              </tr></thead>
              <tbody>
                {detail.operations.flatMap((op) => {
                  const sp = op.work_center_shop_type === 'SPECIAL_PROCESS';
                  const good = Number(op.actual_qty_good)||0, scrap = Number(op.actual_qty_scrap)||0, rework = Number(op.actual_qty_rework)||0, ncp = Number(op.actual_qty_nc)||0;
                  const done = op.status === 'DONE' || op.status === 'CLAIMED';
                  const cellS = { padding: '8px 10px', color: BR.dark, whiteSpace: 'nowrap' };
                  const fmtT = (t) => t ? new Date(t).toTimeString().slice(0,8) : '—';
                  const durOf = (a,c) => { if(!a||!c) return '—'; const s=Math.max(0,Math.floor((new Date(c)-new Date(a))/1000)); const p=(n)=>String(n).padStart(2,'0'); return p(Math.floor(s/3600))+':'+p(Math.floor((s%3600)/60))+':'+p(s%60); };
                  // Setup Time = the operator's actual setup_started_at → setup_completed_at clock
                  // (recorded by the Machine Setup dialog on the operator screen). Runtime is what's
                  // left of the Clock In→Out Duration after that. Falls back to a 20% estimate for
                  // older records that predate real setup clock data. Gated by the per-position toggle
                  // from the Operation Setup screen (op_setup_master); positions not in that list
                  // default to setup-enabled, matching the master screen's own default.
                  const fmtSecs = (s) => { s = Math.max(0, Math.round(s)); const p=(n)=>String(n).padStart(2,'0'); return p(Math.floor(s/3600))+':'+p(Math.floor((s%3600)/60))+':'+p(s%60); };
                  const setupEnabledFor = (seq) => { const m = (opSetupMaster||[]).find(s => s.op_seq === seq); return m ? (m.setup_enabled !== false) : true; };
                  const splitSetupRuntime = (a,c,seq,setupStart,setupEnd) => {
                    if (!a || !c) return { setup: '—', runtime: '—', duration: '—' };
                    const totalSec = Math.max(0, Math.floor((new Date(c)-new Date(a))/1000));
                    if (!setupEnabledFor(seq)) return { setup: '—', runtime: fmtSecs(totalSec), duration: fmtSecs(totalSec) };
                    if (setupStart && setupEnd) {
                      // Setup is clocked as its own, separate session (usually before started_at) —
                      // it doesn't carve into the started_at→claimed_at window, so runtime is that
                      // window in full, and duration is setup + runtime combined (the true
                      // end-to-end elapsed time for this operation).
                      const setupSec = Math.max(0, Math.round((new Date(setupEnd)-new Date(setupStart))/1000));
                      return { setup: fmtSecs(setupSec), runtime: fmtSecs(totalSec), duration: fmtSecs(setupSec + totalSec) };
                    }
                    const setupSec = Math.round(totalSec * 0.2);
                    return { setup: fmtSecs(setupSec), runtime: fmtSecs(totalSec - setupSec), duration: fmtSecs(totalSec) };
                  };
                  const shift = (() => { const t = op.started_at; if (!t) return '—'; const h = new Date(t).getHours(); return h>=6&&h<14 ? 'Shift 1 · 06:00-14:00' : (h>=14&&h<22 ? 'Shift 2 · 14:00-22:00' : 'Shift 3 · 22:00-06:00'); })();
                  if (!sp) {
                    const sess = detailSessions[op.op_seq] || [];
                    const open = expandedDetailOp === op.op_seq;
                    const fmtC = (t) => { const c = new Date(t); return String(c.getHours()).padStart(2,'0')+':'+String(c.getMinutes()).padStart(2,'0')+':'+String(c.getSeconds()).padStart(2,'0'); };
                    const rows = [(<tr key={op.id} onClick={() => sess.length && setExpandedDetailOp(open ? null : op.op_seq)} style={{ borderBottom: `1px solid ${BR.border}`, cursor: sess.length ? 'pointer' : 'default', background: open ? '#fff7ed' : 'transparent' }}>
                      <td style={{...cellS, fontWeight: 700, fontFamily: 'monospace'}}>{sess.length ? (open?'▾ ':'▸ ') : ''}{op.op_seq}</td>
                      <td style={{...cellS, fontWeight: 700}}>{op.op_name}</td>
                      <td style={cellS}>{op.machine_name || '—'}</td>
                      <td style={cellS}>{op.claimed_by_operator_name || '—'}{sess.length>1 ? <span style={{ color: BR.muted, fontWeight:600 }}> +{sess.length-1}</span> : ''}</td>
                      <td style={cellS}>{shift}</td>
                      <td style={cellS}>{op.status==='PENDING' ? '—' : (op.planned_qty!=null ? op.planned_qty : '—')}</td>
                      <td style={cellS}>{good+ncp+scrap+rework}</td>
                      <td style={cellS}>{ncp+scrap+rework}</td>
                      <td style={cellS}>{op.scrap_reason || '—'}</td>
                      <td style={{...cellS, fontWeight: 700}}>{good}</td>
                      <td style={cellS}>{op.actual_qty_rework||0}</td>
                      <td style={{...cellS, color: scrap>0?BR.red:BR.dark, fontWeight: scrap>0?700:400}}>{scrap}</td>
                      <td style={cellS}>{fmtT(op.started_at)}</td>
                      <td style={cellS}>{(op.status==='DONE'||op.status==='CLAIMED') ? fmtT(op.claimed_at) : '—'}</td>
                      {(() => { const sr = splitSetupRuntime(op.started_at, op.claimed_at, op.op_seq, op.setup_started_at, op.setup_completed_at); return (<>
                        <td style={cellS}>{sr.setup}</td>
                        <td style={cellS}>{sr.runtime}</td>
                        <td style={{...cellS, fontWeight: 700, color: BR.dark}}>{sr.duration}</td>
                      </>); })()}
                      <td style={cellS}><Pill>{op.status}</Pill></td>
                      <td style={cellS}>{'—'}</td>
                    </tr>)];
                    if (open && sess.length) {
                      rows.push(<tr key={op.id+'-sess'}><td colSpan={19} style={{ padding: 0, background: '#fbfaf7', borderBottom: `1px solid ${BR.border}` }}>
                        <div style={{ padding: '6px 24px 12px' }}>
                          <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', margin: '6px 0' }}>Operator Sessions · {sess.length}</div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                            <thead><tr style={{ color: BR.muted }}>{['Operator','Machine','Clock In','Clock Out','Duration','Qty'].map(h => (<th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700 }}>{h}</th>))}</tr></thead>
                            <tbody>{sess.map((x, i) => { const co = x.clock_out ? fmtC(x.clock_out) : null; const s = Math.max(0, Math.floor(((x.clock_out ? new Date(x.clock_out) : new Date()) - new Date(x.clock_in)) / 1000)); const p=(n)=>String(n).padStart(2,'0'); const dur = p(Math.floor(s/3600))+':'+p(Math.floor((s%3600)/60))+':'+p(s%60); return (
                              <tr key={i} style={{ borderTop: `1px solid ${BR.border}` }}>
                                <td style={{ padding: '6px 10px', fontWeight: 600, color: BR.dark }}>{x.operator_name || '—'}</td>
                                <td style={{ padding: '6px 10px', color: BR.dark }}>{x.machine_name || '—'}</td>
                                <td style={{ padding: '6px 10px', color: BR.dark }}>{fmtC(x.clock_in)}</td>
                                <td style={{ padding: '6px 10px', color: co ? '#16a34a' : '#f97316', fontWeight: 700 }}>{co ? ('✓ '+co) : '— running —'}</td>
                                <td style={{ padding: '6px 10px', color: BR.dark }}>{dur}</td>
                                <td style={{ padding: '6px 10px', fontWeight: 800, color: BR.dark }}>{x.qty_good || 0}</td>
                              </tr>); })}</tbody>
                          </table>
                        </div>
                      </td></tr>);
                    }
                    return rows;
                  }
                  const qty = Number(op.planned_qty)||0, jig = 20, nb = Math.max(1, Math.ceil(qty/jig));
                  const G = Number(op.actual_qty_good)||0;
                  return Array.from({ length: nb }).map((_, b) => { const bq = Math.min(jig, qty - b*jig);
                    const bgood = Math.max(0, Math.min(bq, G - b*jig));
                    const bl = (batchLog[op.id]||{})[b+1] || null; const bIn = bl && bl.clock_in; const bOut = bl && bl.clock_out;
                    const bst = bOut ? 'COMPLETE' : (bIn ? 'IN_PROGRESS' : (done ? 'COMPLETE' : (op.status === 'IN_PROGRESS' ? (bgood >= bq && bq>0 ? 'COMPLETE' : ((G > b*jig || b === Math.floor(G/jig)) ? 'IN_PROGRESS' : 'PENDING')) : 'PENDING')));
                    return (<tr key={op.id+'-'+b} style={{ borderBottom: `1px solid ${BR.border}`, background: '#fbfaf7' }}>
                      <td style={{...cellS, fontWeight: 700, fontFamily: 'monospace'}}>{op.op_seq}_{(b+1)*10}</td>
                      <td style={{...cellS, fontWeight: 700}}>{op.op_name}</td>
                      <td style={cellS}>{op.machine_name || '—'}</td>
                      <td style={cellS}>{op.claimed_by_operator_name || '—'}</td>
                      <td style={cellS}>{shift}</td>
                      <td style={cellS}>{bst==='PENDING' ? '—' : bq}</td>
                      <td style={cellS}>{bgood}</td>
                      <td style={cellS}>0</td>
                      <td style={cellS}>{'—'}</td>
                      <td style={{...cellS, fontWeight: 700}}>{bgood}</td>
                      <td style={cellS}>0</td>
                      <td style={cellS}>0</td>
                      <td style={cellS}>{bIn ? fmtT(bIn) : (done || bst === 'IN_PROGRESS' ? fmtT(op.started_at) : '—')}</td>
                      <td style={cellS}>{bOut ? fmtT(bOut) : (bst === 'COMPLETE' ? fmtT(op.claimed_at) : '—')}</td>
                      <td style={cellS}>{'—'}</td>
                      <td style={cellS}>{'—'}</td>
                      <td style={{...cellS, fontWeight: 700, color: BR.dark}}>{(bIn&&bOut) ? durOf(bIn,bOut) : ((bIn&&!bOut) ? '—' : durOf(op.started_at, bst==='COMPLETE'?op.claimed_at:null))}</td>
                      <td style={cellS}><Pill>{bst}</Pill></td>
                      <td style={cellS}>{(bst === 'COMPLETE' || bst === 'IN_PROGRESS') ? <button onClick={() => setProcData({ op, wo: detail.wo, batch: b })} style={{ padding: '4px 8px', background: BR.blue, color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>Process Data</button> : '—'}</td>
                    </tr>); });
                })}
              </tbody>
            </table>
            </div>
          </div>
          </div>
        )}
      </div>

      {procData && (() => {
        const op = procData.op, wo = procData.wo, onlyB = procData.batch;
        const qty = Number(op.planned_qty) || Number(wo.qty_ordered) || 0;
        const jig = 20; const nb = Math.max(1, Math.ceil(qty / jig));
        const batches = (onlyB != null) ? [onlyB] : Array.from({ length: nb }, (_, i) => i);
        const baseMs = op.started_at ? new Date(op.started_at).getTime() : new Date().getTime();
        const params = procParams(op);
        const clk = (ms) => { const d = new Date(ms); return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0'); };
        return (
        <div onClick={() => setProcData(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 20, width: '92vw', maxWidth: 1000, maxHeight: '92vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: BR.dark }}>Process Data — {op.op_name}{onlyB != null ? ' · Batch ' + (onlyB + 1) + '/' + nb : ''}</div>
                <div style={{ fontSize: 12, color: BR.muted, marginTop: 2 }}>{wo.wo_number} · {wo.part_name} ({wo.part_number}) · {op.work_center_code}</div>
              </div>
              <button onClick={() => setProcData(null)} style={{ background: 'none', border: 'none', color: BR.muted, cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: BR.dark, background: BR.bg, border: `1px solid ${BR.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
              <span>WO Qty: <strong>{qty}</strong></span><span>Jig: <strong>{jig} pcs</strong></span><span>Batches: <strong>{nb}</strong></span><span>Parameters: <strong>{params.length ? params.map(pp => pp.label.replace(/ \(.*/, '')).join(' · ') : 'setup — no readings'}</strong></span>
            </div>
            {params.length === 0 ? (<div style={{ padding: 24, textAlign: 'center', color: BR.muted, fontSize: 13, border: `1px dashed ${BR.border}`, borderRadius: 8 }}>This is a setup operation (Jigging) — no in-process readings are recorded.</div>) : batches.map((b) => { const startMs = baseMs + b * 30 * 60000; const bq = Math.min(jig, qty - b * jig);
              return (<div key={b} style={{ border: `1px solid ${BR.border}`, borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ background: BR.orange, color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 800 }}>Batch {b + 1} / {nb}</span>
                  <span style={{ fontSize: 12, color: BR.dark }}>{bq} pcs · Jig <strong>JG-TI-230{b + 1}</strong> · <strong>{clk(startMs)}–{clk(startMs + 30 * 60000)}</strong></span>
                </div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  {params.map((param) => { const pts = []; for (let i = 0; i <= 8; i++) { pts.push({ t: i * 4, val: param.gen(b, i) }); }
                    return (<ProcChart key={param.k} pts={pts} keyName="val" color={param.color} label={param.label} unit={param.unit} mn={param.mn} mx={param.mx} startMs={startMs} />); })}
                </div>
              </div>);
            })}
          </div>
        </div>);
      })()}

      {editWo && <EditModal wo={editWo} customerId={customerId} onClose={() => setEditWo(null)} onSaved={() => { setEditWo(null); setInfo('Work order updated'); setTimeout(() => setInfo(null), 3000); refresh(); }} />}
      <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={refresh}
                   customerId={customerId} workCenters={workCenters} operationMaster={operationMaster} machineCaps={machineCaps} recipeMaster={recipeMaster} components={components} spRecipes={spRecipes} opSetupMaster={opSetupMaster} />
    </div>
  );
}
